import { queryClient } from '@/router'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { type z } from 'zod'

import { getNvcCategoriesQuery } from '@/lib/queries/business/nvc-category/queries'
import { updateCaseExtraInfoMutation } from '@/lib/queries/business/v1/case-extra/mutations'
import {
  caseInformationWithHistoryQuery,
  getCaseInformationWithHistoryQueryKey,
} from '@/lib/queries/business/v2/case/queries'
import { getCaseExtraInfoQuery, getCaseExtraInfoQueryKey } from '@/lib/queries/business/v3/case-extra/queries'
import { getLocalDate } from '@/lib/utils/datetime'

import { useFindTargetCase } from '../../../hooks/use-find-target-case'
import { type AddMoreInformationDialogProps } from './add-more-information-dialog'
import { formSchema } from './validations'

type UseAddMoreInformationDialogParams = Pick<AddMoreInformationDialogProps, 'closeDialog'>

export const useAddMoreInformationDialog = ({ closeDialog }: UseAddMoreInformationDialogParams) => {
  const { targetCase } = useFindTargetCase()
  const caseId = targetCase?.case_id

  const { data: dataCaseExtraInfo } = useQuery(getCaseExtraInfoQuery(caseId))
  const { data: dataCaseInformation } = useSuspenseQuery(caseInformationWithHistoryQuery(caseId))

  const { applicationFiledDate, categoryCode, interviewDate } = dataCaseExtraInfo ?? {}
  const { nvcInformation } = dataCaseInformation ?? {}

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      location: '',
      category: '',
      dateFiled: undefined,
      interviewDate: undefined,
    },
  })

  const { data: dataNvcCategories } = useQuery(getNvcCategoriesQuery(nvcInformation?.visaType))

  const { mutateAsync: updateCaseExtraInfo, isPending: isPendingUpdateCaseExtraInfo } =
    useMutation(updateCaseExtraInfoMutation)

  const handleSubmitForm: SubmitHandler<z.infer<typeof formSchema>> = async (data) => {
    if (!caseId) {
      return
    }

    await updateCaseExtraInfo(
      {
        caseId,
        categoryCode: data.category,
        applicationFiledDate: dayjs(data.dateFiled).format('YYYY-MM-DD'),
        interviewDate: dayjs(data.interviewDate).format('YYYY-MM-DD'),
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: getCaseInformationWithHistoryQueryKey(caseId),
          })
          await queryClient.invalidateQueries({
            queryKey: getCaseExtraInfoQueryKey(caseId),
          })
          closeDialog()
        },
      }
    )
  }

  useEffect(() => {
    form.reset({
      location: nvcInformation?.location ?? '',
      category: categoryCode,
      dateFiled: applicationFiledDate ? getLocalDate(applicationFiledDate) : undefined,
      interviewDate: interviewDate ? getLocalDate(interviewDate) : undefined,
    })
  }, [form, nvcInformation?.location, categoryCode, applicationFiledDate, interviewDate])

  return {
    form,
    dataNvcCategories,
    isLoadingSaveButton: isPendingUpdateCaseExtraInfo,
    handleSubmitForm,
  }
}
